// WHAT: Custom hook for synchronizing Yazio nutrition data with backend.
// WHY:  Provides state management and functions for syncing Yazio nutrition data, supporting both today's data and custom dates with force sync options.
// DOMAIN: Health → Yazio
// DEPENDENCIES: React, @tauri-apps/api
// RISKS:
//   - WARN: Tauri command 'sync_yazio_nutrition_data' must exist in backend; failures will throw errors.
//   - NOTE: Error messages are generic; detailed error information may be lost.
//   - WARN: Force sync bypasses cache; may cause rate limiting with frequent calls.

import { core } from "@tauri-apps/api";
import { useState } from "react";

export interface YazioSyncRequest {
  date?: string;
  force_sync?: boolean;
}

export interface YazioSyncResponse {
  carbs: number;
  data_synced: boolean;
  date: string;
  fat: number;
  message: string;
  protein: number;
  success: boolean;
  total_calories: number;
}

interface YazioSyncState {
  error: string | undefined;
  isLoading: boolean;
  lastSyncDate: Date | undefined;
  lastSyncResult: undefined | YazioSyncResponse;
}

/**
 * WHAT:
 *   Custom hook for synchronizing Yazio nutrition data with backend.
 *
 * WHY:
 *   Provides centralized state management and functions for syncing Yazio nutrition data,
 *   supporting both today's data and custom dates. Includes force sync options to bypass cache.
 *
 * RETURNS:
 *   - isLoading: boolean - Loading state during sync operations
 *   - error: string | undefined - Error message if sync fails
 *   - lastSyncResult: YazioSyncResponse | undefined - Result of last sync operation
 *   - lastSyncDate: Date | undefined - Timestamp of last sync
 *   - syncNutritionData: (request?: YazioSyncRequest) => Promise<YazioSyncResponse> - Generic sync function
 *   - syncTodayData: () => Promise<YazioSyncResponse> - Sync today's data
 *   - syncDateData: (date: string) => Promise<YazioSyncResponse> - Sync data for specific date
 *   - forceSyncToday: () => Promise<YazioSyncResponse> - Force sync today's data (bypass cache)
 *   - forceSyncDate: (date: string) => Promise<YazioSyncResponse> - Force sync data for specific date (bypass cache)
 *   - clearError: () => void - Clear error state
 *   - clearLastSync: () => void - Clear last sync result and date
 *
 * SIDE EFFECTS:
 *   - Updates loading state during sync operations
 *   - Updates error state on sync failures
 *   - Updates lastSyncResult and lastSyncDate on successful syncs
 *   - Invokes Tauri command 'sync_yazio_nutrition_data' for backend synchronization
 *
 * RISKS:
 *   - WARN: Tauri command 'sync_yazio_nutrition_data' must exist in backend; failures will throw errors.
 *   - NOTE: Error messages are generic; detailed error information may be lost.
 *   - WARN: Force sync bypasses cache; may cause rate limiting with frequent calls.
 *   - NOTE: Date format validation is not performed; invalid dates may cause backend errors.
 *
 * @returns Object with sync state and functions
 *
 */
export function useYazioSync() {
  const [state, setState] = useState<YazioSyncState>({
    error: undefined,
    isLoading: false,
    lastSyncDate: undefined,
    lastSyncResult: undefined,
  });

  /**
   * WHAT:
   *   Generic function to sync Yazio nutrition data with optional date and force sync parameters.
   *
   * WHY:
   *   Provides a unified interface for all sync operations, reducing code duplication.
   *
   * INPUTS:
   *   - request?: YazioSyncRequest - Optional request object with date and force_sync flags
   *
   * OUTPUT:
   *   - Promise<YazioSyncResponse> - Sync response with success status, message, and nutrition data
   *
   * SIDE EFFECTS:
   *   - Sets isLoading to true at start
   *   - Clears error state
   *   - Invokes Tauri command 'sync_yazio_nutrition_data'
   *   - Updates lastSyncResult and lastSyncDate on success
   *   - Sets error state on failure
   *   - Sets isLoading to false on completion
   *
   * RISKS:
   *   - WARN: Tauri command must exist; failures will throw errors.
   *   - NOTE: Error message is generic; original error details are lost.
   *
   * @param request - Optional request object with date and force_sync flags
   *
   * @returns Promise resolving to sync response
   *
   */
  const syncNutritionData = async (request: YazioSyncRequest = {}) => {
    try {
      setState((previous) => ({ ...previous, error: undefined, isLoading: true }));

      const response = await core.invoke<YazioSyncResponse>("sync_yazio_nutrition_data", {
        request,
      });

      setState((previous) => ({
        ...previous,
        isLoading: false,
        lastSyncDate: new Date(),
        lastSyncResult: response,
      }));

      return response;
    } catch (error) {
      // WARN: Failed to sync Yazio nutrition data
      setState((previous) => ({
        ...previous,
        error: "Failed to sync nutrition data",
        isLoading: false,
      }));
      throw error;
    }
  };

  /**
   * WHAT:
   *   Syncs today's Yazio nutrition data.
   *
   * WHY:
   *   Convenience wrapper for syncing today's data without specifying date.
   *
   * OUTPUT:
   *   - Promise<YazioSyncResponse> - Sync response
   *
   * @returns Promise resolving to sync response
   *
   */
  const syncTodayData = () => {
    return syncNutritionData();
  };

  /**
   * WHAT:
   *   Syncs Yazio nutrition data for a specific date.
   *
   * WHY:
   *   Allows syncing historical nutrition data for analysis and reporting.
   *
   * INPUTS:
   *   - date: string - Date string in format expected by backend
   *
   * OUTPUT:
   *   - Promise<YazioSyncResponse> - Sync response
   *
   * RISKS:
   *   - NOTE: Date format validation is not performed; invalid dates may cause backend errors.
   *
   * @param date - Date string in format expected by backend
   *
   * @returns Promise resolving to sync response
   *
   */
  const syncDateData = (date: string) => {
    return syncNutritionData({ date });
  };

  /**
   * WHAT:
   *   Force syncs today's Yazio nutrition data, bypassing cache.
   *
   * WHY:
   *   Allows users to force refresh today's data even if recently synced.
   *
   * OUTPUT:
   *   - Promise<YazioSyncResponse> - Sync response
   *
   * RISKS:
   *   - WARN: Force sync bypasses cache; may cause unnecessary API calls and rate limiting.
   *
   * @returns Promise resolving to sync response
   *
   */
  const forceSyncToday = () => {
    return syncNutritionData({ force_sync: true });
  };

  /**
   * WHAT:
   *   Force syncs Yazio nutrition data for a specific date, bypassing cache.
   *
   * WHY:
   *   Allows users to force refresh historical data even if recently synced.
   *
   * INPUTS:
   *   - date: string - Date string in format expected by backend
   *
   * OUTPUT:
   *   - Promise<YazioSyncResponse> - Sync response
   *
   * RISKS:
   *   - WARN: Force sync bypasses cache; may cause unnecessary API calls and rate limiting.
   *   - NOTE: Date format validation is not performed; invalid dates may cause backend errors.
   *
   * @param date - Date string in format expected by backend
   *
   * @returns Promise resolving to sync response
   *
   */
  const forceSyncDate = (date: string) => {
    return syncNutritionData({ date, force_sync: true });
  };

  /**
   * WHAT:
   *   Clears the current error state.
   *
   * WHY:
   *   Allows users to dismiss error messages and retry operations.
   *
   * SIDE EFFECTS:
   *   - Sets error state to undefined
   *
   * @returns void
   *
   */
  const clearError = () => {
    setState((previous) => ({ ...previous, error: undefined }));
  };

  /**
   * WHAT:
   *   Clears the last sync result and date.
   *
   * WHY:
   *   Allows resetting sync history for testing or troubleshooting.
   *
   * SIDE EFFECTS:
   *   - Sets lastSyncResult and lastSyncDate to undefined
   *
   * @returns void
   *
   */
  const clearLastSync = () => {
    setState((previous) => ({
      ...previous,
      lastSyncDate: undefined,
      lastSyncResult: undefined,
    }));
  };

  return {
    ...state,
    clearError,
    clearLastSync,
    forceSyncDate,
    forceSyncToday,
    syncDateData,
    syncNutritionData,
    syncTodayData,
  };
}
